import { useState, useRef, useEffect, useContext } from "react"

import { Avatar } from '@mui/material'
import SmartToy from '@mui/icons-material/SmartToy'
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined'
import FiberManualRecordOutlined from '@mui/icons-material/FiberManualRecordOutlined'
import StopCircleOutlined from '@mui/icons-material/StopCircleOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import CheckOutlined from '@mui/icons-material/CheckOutlined'

import defaultAvatar1 from "../../../icon/defaultAvatars/user.png"

import ScreenshotRegionSelector from './ScreenshotRegionSelector'
import AiMarkdown from './AiMarkdown'

import { TelemetryContext } from '../context/TelemetryContext'
import { useRegion } from '../hooks/useRegion'

import { CHAT_EVENTS, REGIONS } from '../constants/telemetryEvents'

import '../css/workspace.scss'

const RECORD_DURATION_MS = 10000
const RECORD_INTERVAL_MS = 1000

// Helper to clean up the timestamp (e.g. "10:30 AM")
const formatTime = (isoString) => {
  if (!isoString) return ""
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// Words as the backend counts them: the text split on whitespace, empty parts ignored.
const countWords = (text) => text.split(/\s+/).filter(Boolean).length

// 1042 -> "1,042"
const formatCount = (n) => n.toLocaleString('en-US')

export default function ChatWindow({
  messages,
  onSendMessage,
  currentUserName,
  currentUserId,
  aiId,
  isAiProcessing,
  onRequestScreenshot,
  onRequestBurst,
  onRequestRegionCapture,
  // Optional line shown just above the input (e.g. an experiment's OffloadingStrip).
  notice = null,
  // Optional cap on the words a student can send to the AI (study conditions pass 1000).
  // Without it the chat keeps its 500-character counter exactly as before.
  maxWords = null,
  // The AI's replies rendered as Markdown (AiMarkdown; study conditions pass true). The
  // student's own messages, and every message without it, stay plain text as before.
  markdownReplies = false,
}) {
  const { logTelemetryEvent } = useContext(TelemetryContext)

  const [chatInput, setChatInput] = useState("")
  // { kind: 'screenshot', frames: [{screenshot,...}] } | { kind: 'burst', frames: [...] }
  const [pendingAttachment, setPendingAttachment] = useState(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [recordProgress, setRecordProgress] = useState({ done: 0, total: 0 })
  const [captureError, setCaptureError] = useState(null)
  // Base (uncropped) whole-workspace capture awaiting the student's region selection - see
  // handleRegionCaptureClick/ScreenshotRegionSelector. Distinct from onRequestScreenshot's
  // flow (regular GUI sessions), which attaches immediately with no selection step.
  const [regionCaptureBaseImage, setRegionCaptureBaseImage] = useState(null)
  // Which AI message's Copy button was just clicked, for a brief "Copied" confirmation -
  // keyed by message id so multiple messages don't share one flash state.
  const [copiedMessageId, setCopiedMessageId] = useState(null)
  const chatHistoryRef = useRef(null)
  const containerRef = useRef(null)

  // Register Region
  useRegion(REGIONS.CHAT_WINDOW, containerRef, 10)

  const CHARACTER_LIMIT = 500

  // With maxWords the word limit replaces the character counter: a long paste still goes
  // into the box, but Send (button and Enter) does nothing until it is short enough, and a
  // note under the input says why. The counter shows only once the draft passes 80% of the
  // limit. The backend refuses longer messages too.
  const wordLimit = Number(maxWords) > 0 ? Number(maxWords) : null
  const wordCount = wordLimit ? countWords(chatInput) : 0
  const isOverWordLimit = wordLimit !== null && wordCount > wordLimit
  const showsWordCount = wordLimit !== null && wordCount > wordLimit * 0.8
  // What greys out Send besides an empty box or a busy AI: past 500 characters as before,
  // or, with a word limit, only past that limit.
  const isOverLimit = wordLimit !== null ? isOverWordLimit : chatInput.length > CHARACTER_LIMIT

  // Track last selections to avoid duplicates
  const lastInputSelection = useRef('')
  const lastHistorySelection = useRef('')

  const isPasting = useRef(false)

  // Scroll on messages change or when AI starts processing: to the end, except that an AI reply
  // taller than the history opens at its beginning - at its end, its start was out of view
  // above and read as cut off (E2 survey).
  useEffect(() => {
    const history = chatHistoryRef.current
    if (!history) return
    const rows = history.querySelectorAll(':scope > .ws-message')
    const last = rows[rows.length - 1]
    const latest = messages[messages.length - 1]
    if (!isAiProcessing && latest?.sender_id === aiId && last && last.offsetHeight > history.clientHeight) {
      history.scrollTop += last.getBoundingClientRect().top - history.getBoundingClientRect().top - 8
      return
    }
    history.scrollTop = history.scrollHeight
  }, [messages, isAiProcessing, aiId])

  // Track selection and copy in chat history
  useEffect(() => {
    const el = chatHistoryRef.current
    if (!el) return

    const handleHistorySelection = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastHistorySelection.current) {
        lastHistorySelection.current = selectedText
        logTelemetryEvent(CHAT_EVENTS.SELECT_HISTORY, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastHistorySelection.current = ''
      }
    }

    el.addEventListener('mouseup', handleHistorySelection)
    el.addEventListener('keyup', handleHistorySelection)

    return () => {
      el.removeEventListener('mouseup', handleHistorySelection)
      el.removeEventListener('keyup', handleHistorySelection)
    }
  }, [logTelemetryEvent])

  const handleSubmit = (e) => {
    e.preventDefault()
    // Prevent sending if empty OR if AI is processing
    if (!chatInput.trim() || isAiProcessing) return
    // Over the word limit nothing is sent; the draft stays in the box to be shortened.
    if (isOverWordLimit) return

    if (pendingAttachment) {
      logTelemetryEvent(CHAT_EVENTS.ATTACHMENT, {
        attachment_type: pendingAttachment.kind,
        frame_count: pendingAttachment.frames.length,
        timestamp: Date.now(),
      })
    }

    onSendMessage(chatInput, pendingAttachment)
    setChatInput("")
    setPendingAttachment(null)
    setCaptureError(null)
  }

  // Screenshots/recordings the student explicitly attaches are a deliberate action, distinct
  // from the passive visualTraceEnabled background capture - see ChatContext.sendMessage,
  // which bypasses aiVisualContextEnabled specifically for a manualAttachment.
  // requestSnapshot/requestBurst resolve to null/empty with no thrown error when the preview
  // iframe has never been rendered (previewMode: 'manual' - the student hasn't clicked Render
  // yet), so that's the only failure case to surface here; give it an explicit message instead
  // of the button silently doing nothing.
  const handleScreenshotClick = async () => {
    if (!onRequestScreenshot || isCapturing || isRecording) return
    setIsCapturing(true)
    setCaptureError(null)
    try {
      const frame = await onRequestScreenshot()
      if (frame?.screenshot) {
        setPendingAttachment({ kind: 'screenshot', frames: [frame] })
      } else {
        setCaptureError('Render your page first, then try attaching a screenshot again.')
      }
    } finally {
      setIsCapturing(false)
    }
  }

  // Whole-workspace capture (InitialFinal/InitialFinalCSS) - captures first, then opens a
  // region-selection step before anything is attached, unlike handleScreenshotClick's
  // immediate attach. See Workspace.jsx's requestWorkspaceRegionCapture for what's captured.
  const handleRegionCaptureClick = async () => {
    if (!onRequestRegionCapture || isCapturing || isRecording) return
    setIsCapturing(true)
    setCaptureError(null)
    try {
      const dataUrl = await onRequestRegionCapture()
      if (dataUrl) {
        setRegionCaptureBaseImage(dataUrl)
      } else {
        setCaptureError('Render your page first, then try attaching a screenshot again.')
      }
    } finally {
      setIsCapturing(false)
    }
  }

  const handleRegionCaptureConfirm = (croppedDataUrl) => {
    setRegionCaptureBaseImage(null)
    if (croppedDataUrl) {
      setPendingAttachment({ kind: 'screenshot', frames: [{ screenshot: croppedDataUrl }] })
    }
  }

  const handleRegionCaptureCancel = () => setRegionCaptureBaseImage(null)

  const handleRecordClick = async () => {
    if (!onRequestBurst || isCapturing || isRecording) return
    setIsRecording(true)
    setRecordProgress({ done: 0, total: 0 })
    setCaptureError(null)
    try {
      const frames = await onRequestBurst(RECORD_DURATION_MS, RECORD_INTERVAL_MS, (done, total) => {
        setRecordProgress({ done, total })
      })
      const withScreenshots = frames.filter((f) => f?.screenshot)
      if (withScreenshots.length > 0) {
        setPendingAttachment({ kind: 'burst', frames: withScreenshots })
      } else {
        setCaptureError('Render your page first, then try recording a clip again.')
      }
    } finally {
      setIsRecording(false)
    }
  }

  const handleKeyDown = (e) => {
    // Handle Send
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e)
    }

    // EVENT: CHAT_BACKSPACE / CHAT_DELETE
    if (e.key === 'Backspace' || e.key === 'Delete') {
        const start = e.target.selectionStart
        const end = e.target.selectionEnd
        const isRangeDelete = end - start > 0

        let predictedText = ""

        if (isRangeDelete) {
            // Combine text before selection and after selection
            predictedText = chatInput.slice(0, start) + chatInput.slice(end)
        } else {
            // Single Character Delete
            if (e.key === 'Backspace') {
                if (start === 0) return // Nothing to backspace
                predictedText = chatInput.slice(0, start - 1) + chatInput.slice(start)
            } else {
                // 'Delete' key
                predictedText = chatInput.slice(0, start) + chatInput.slice(start + 1)
            }
        }

        logTelemetryEvent(CHAT_EVENTS.DELETE, {
            timestamp: Date.now(),
            cursor_position: start,
            selection_end: end,
            text_before: chatInput,
            text_after: predictedText, 
            is_range_delete: isRangeDelete,
            key_used: e.key
        })
    }
  }

  const handleInputChange = (e) => {
    const val = e.target.value
    const prevLength = chatInput.length
    
    setChatInput(val)
    
    // Only log TYPE if text was added (not deleted or pasted)
    if (val.length > prevLength && !isPasting.current) {
      logTelemetryEvent(CHAT_EVENTS.TYPE, {
        text: val, 
        timestamp: Date.now()
      })
    }
    
    isPasting.current = false
  }

  const handleInputSelect = (e) => {
    const start = e.target.selectionStart
    const end = e.target.selectionEnd
    const selectedText = e.target.value.substring(start, end)
    
    if (selectedText && selectedText !== lastInputSelection.current) {
      lastInputSelection.current = selectedText
      logTelemetryEvent(CHAT_EVENTS.SELECT_INPUT, {
        selected_text: selectedText,
        timestamp: Date.now()
      })
    } else if (!selectedText) {
      lastInputSelection.current = ''
    }
  }

  const handlePaste = (e) => {
    isPasting.current = true
    const pastedText = e.clipboardData.getData('text')
    logTelemetryEvent(CHAT_EVENTS.PASTE, {
      pasted_text: pastedText,
      timestamp: Date.now()
    })
  }

  // EVENT: CHAT_COPY
  const handleCopy = (e) => {
      let selectedText = "";

      // Copying from the Input Box
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
          const start = e.target.selectionStart;
          const end = e.target.selectionEnd;
          selectedText = e.target.value.substring(start, end);
          
          if (selectedText && selectedText.length > 0) {
              logTelemetryEvent(CHAT_EVENTS.COPY_INPUT, {
                  selected_text: selectedText,
                  timestamp: Date.now()
              })
          }
      } 
      // Copying from Chat History
      else {
          selectedText = window.getSelection().toString();
          
          if (selectedText && selectedText.length > 0) {
              logTelemetryEvent(CHAT_EVENTS.COPY_HISTORY, {
                  selected_text: selectedText,
                  timestamp: Date.now()
              })
          }
      }
  }

  // One-click alternative to select-and-copy: the professor's ask ("make the copy-paste
  // from the chat to the HTML IDE easier") is mostly a friction problem - a student
  // manually selecting a full multi-line HTML document (doctype through </html>) out of a
  // chat bubble is easy to get wrong (a missed opening/closing line). A single button that
  // copies the exact message text sidesteps that regardless of session type.
  const handleCopyMessage = (msg) => {
    navigator.clipboard.writeText(msg.content).then(() => {
      setCopiedMessageId(msg.id)
      setTimeout(() => setCopiedMessageId((current) => (current === msg.id ? null : current)), 1500)
      logTelemetryEvent(CHAT_EVENTS.COPY_HISTORY, {
        selected_text: msg.content,
        via: 'button',
        timestamp: Date.now()
      })
    }).catch((err) => console.error('Copy to clipboard failed:', err))
  }

  return (
    <div ref={containerRef} className="ws-chat-container">
      <div className="ws-chat">
        <div 
            className="ws-chat-history" 
            ref={chatHistoryRef}
            onCopy={handleCopy} 
        >
          {messages.map((msg, index) => {
            const isOwner = msg.sender_id === currentUserId
            const isAI = msg.sender_id === aiId
            const avatarUrl = isAI ? null : defaultAvatar1
            const displayName = isAI ? "AI Assistant" : currentUserName

            return (
              <div key={msg.id || index} className={`ws-message ${isOwner ? 'ws-owner' : ''}`}>
                <div className="ws-message-info">
                  {isAI ? (
                    <Avatar sx={{ bgcolor: '#1976d2', width: 38, height: 38 }}>
                      <SmartToy sx={{ fontSize: '1rem' }} />
                    </Avatar>
                  ) : (
                    <img src={avatarUrl} alt="user avatar" />
                  )}
                  <span className="ws-info-name">{displayName}</span>
                  <span className="ws-info-time">{formatTime(msg.timestamp)}</span>
                </div>
                <div className="ws-message-content-wrapper">
                  <div className="ws-message-content">
                    {msg.attachmentThumbnail && (
                      <div style={{ marginBottom: '6px' }}>
                        <img
                          src={msg.attachmentThumbnail}
                          alt={msg.attachmentCount > 1 ? `${msg.attachmentCount}-frame attachment` : 'Attached screenshot'}
                          style={{ maxWidth: '160px', maxHeight: '120px', borderRadius: '6px', border: '1px solid #e5e5e5', display: 'block' }}
                        />
                        {msg.attachmentCount > 1 && (
                          <span style={{ fontSize: '0.7rem', color: '#737373' }}>{msg.attachmentCount}-frame clip attached</span>
                        )}
                      </div>
                    )}
                    {isAI && markdownReplies
                      ? <AiMarkdown text={msg.content} />
                      : <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{msg.content}</pre>}
                  </div>
                  {isAI && (
                    <button
                      onClick={() => handleCopyMessage(msg)}
                      title="Copy this response"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px',
                        padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600,
                        border: '1px solid #e5e5e5', borderRadius: '4px', background: '#ffffff',
                        color: copiedMessageId === msg.id ? '#059669' : '#4b5563', cursor: 'pointer',
                      }}
                    >
                      {copiedMessageId === msg.id ? (
                        <><CheckOutlined style={{ fontSize: '0.85rem' }} /> Copied</>
                      ) : (
                        <><ContentCopyOutlined style={{ fontSize: '0.85rem' }} /> Copy</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )
          })}

          {/* Thinking Indicator */}
          {isAiProcessing && (
             <div className="ws-message">
               <div className="ws-message-info">
                 <Avatar sx={{ bgcolor: '#1976d2', width: 38, height: 38 }}>
                    <SmartToy sx={{ fontSize: '1rem' }} />
                 </Avatar>
                 <span className="ws-info-name">AI Assistant</span>
               </div>
               <div className="ws-message-content-wrapper">
                 <div className="ws-message-content" style={{ 
                     color: '#888', 
                     fontStyle: 'italic',
                     marginTop: '28px' 
                 }}>
                   Thinking...
                 </div>
               </div>
             </div>
          )}
        </div>

        {(onRequestScreenshot || onRequestBurst || onRequestRegionCapture) && (
          <div style={{ padding: '0 12px', background: '#ffffff' }}>
            {pendingAttachment && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 8px', margin: '8px 0 0', borderRadius: '6px',
                background: '#f3f4f6', border: '1px solid #e5e5e5',
              }}>
                <img
                  src={pendingAttachment.frames[0].screenshot}
                  alt=""
                  style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #d1d5db' }}
                />
                <span style={{ fontSize: '0.78rem', color: '#4b5563', flex: 1 }}>
                  {pendingAttachment.kind === 'burst'
                    ? `${pendingAttachment.frames.length}-frame clip attached`
                    : 'Screenshot attached'}
                </span>
                <button
                  type="button"
                  onClick={() => setPendingAttachment(null)}
                  title="Remove attachment"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#737373', display: 'flex', padding: '2px' }}
                >
                  <CloseIcon style={{ fontSize: '1rem' }} />
                </button>
              </div>
            )}
            {isRecording && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 8px', margin: '8px 0 0', borderRadius: '6px',
                background: '#fef2f2', border: '1px solid #fecaca',
              }}>
                <FiberManualRecordOutlined style={{ fontSize: '0.9rem', color: '#dc2626' }} />
                <span style={{ fontSize: '0.78rem', color: '#991b1b' }}>
                  Recording... {recordProgress.total ? `${recordProgress.done}/${recordProgress.total}s` : ''}
                </span>
              </div>
            )}
            {captureError && !isRecording && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 8px', margin: '8px 0 0', borderRadius: '6px',
                background: '#fef2f2', border: '1px solid #fecaca',
              }}>
                <span style={{ fontSize: '0.78rem', color: '#991b1b', flex: 1 }}>{captureError}</span>
                <button
                  type="button"
                  onClick={() => setCaptureError(null)}
                  title="Dismiss"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', display: 'flex', padding: '2px' }}
                >
                  <CloseIcon style={{ fontSize: '1rem' }} />
                </button>
              </div>
            )}
          </div>
        )}

        {notice}

        <form className="ws-message-form" onSubmit={handleSubmit}>
          {(onRequestScreenshot || onRequestBurst || onRequestRegionCapture) && (
            <div style={{ display: 'flex', gap: '2px' }}>
              {(onRequestScreenshot || onRequestRegionCapture) && (
                <button
                  type="button"
                  onClick={onRequestRegionCapture ? handleRegionCaptureClick : handleScreenshotClick}
                  disabled={isCapturing || isRecording || isAiProcessing}
                  title={onRequestRegionCapture ? "Attach a screenshot of your workspace" : "Attach a screenshot of your rendered output"}
                  style={{
                    background: 'none', border: 'none', cursor: (isCapturing || isRecording || isAiProcessing) ? 'default' : 'pointer',
                    color: '#4b5563', opacity: (isCapturing || isRecording || isAiProcessing) ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', padding: '6px',
                  }}
                >
                  <PhotoCameraOutlined style={{ fontSize: '1.15rem' }} />
                </button>
              )}
              {onRequestBurst && (
                <button
                  type="button"
                  onClick={handleRecordClick}
                  disabled={isCapturing || isRecording || isAiProcessing}
                  title="Record a short clip (up to 10s) of your rendered output"
                  style={{
                    background: 'none', border: 'none', cursor: (isCapturing || isRecording || isAiProcessing) ? 'default' : 'pointer',
                    color: isRecording ? '#dc2626' : '#4b5563', opacity: (isCapturing || isAiProcessing) ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', padding: '6px',
                  }}
                >
                  {isRecording ? <StopCircleOutlined style={{ fontSize: '1.15rem' }} /> : <FiberManualRecordOutlined style={{ fontSize: '1.15rem' }} />}
                </button>
              )}
            </div>
          )}
          <textarea
            className="ws-message-input"
            // Disable input while thinking
            placeholder={isAiProcessing ? "AI is typing..." : "Ask a question..."}
            rows={1}
            value={chatInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={handleInputSelect}
            onPaste={handlePaste}
            onCopy={handleCopy}
            disabled={isAiProcessing}
          />
          {wordLimit === null ? (
            <div className="ws-char-count">
              <span style={{
                color: chatInput.length > CHARACTER_LIMIT ? '#d32f2f' : '#999',
                fontWeight: chatInput.length > CHARACTER_LIMIT ? 'bold' : 'normal'
              }}>
                {chatInput.length}/{CHARACTER_LIMIT}
              </span>
            </div>
          ) : showsWordCount && (
            <div className="ws-char-count">
              <span style={{
                color: isOverWordLimit ? '#d32f2f' : '#999',
                fontWeight: isOverWordLimit ? 'bold' : 'normal'
              }}>
                {`${formatCount(wordCount)}/${formatCount(wordLimit)} words`}
              </span>
            </div>
          )}
          {/* Disable button while thinking */}
          <button
            type="submit"
            className="ws-send-button"
            disabled={isAiProcessing || !chatInput.trim() || isOverLimit}
            style={{ opacity: (isAiProcessing || !chatInput.trim() || isOverLimit) ? 0.5 : 1 }}
          >
            Send
          </button>
        </form>

        {isOverWordLimit && (
          <div style={{ flexShrink: 0, margin: '0 12px 10px', fontSize: '0.78rem', color: '#991b1b' }}>
            {`${formatCount(wordCount)} words - the limit is ${formatCount(wordLimit)}. Shorten your message to send it.`}
          </div>
        )}
      </div>
      <ScreenshotRegionSelector
        open={!!regionCaptureBaseImage}
        imageDataUrl={regionCaptureBaseImage}
        onCancel={handleRegionCaptureCancel}
        onConfirm={handleRegionCaptureConfirm}
      />
    </div>
  )
}